<script setup lang="ts">
import { h, reactive, ref } from "vue";
import AdminLayout from "./AdminLayout.vue";
import { AdminFetcher } from "../../api/adminFetcher";
import { toastErr, toastOk } from "../../utils/toasts";
import { RowData } from "naive-ui/es/data-table/src/interface";
import {
  DataTableColumns,
  DataTableFilterState,
  NButton,
  NDataTable,
  NInput,
  NInputGroup,
  NSpace,
} from "naive-ui";

type AiSummary = {
  companyId: string;
  ai_summary: boolean;
  companyName: string;
  companyTarif: string;
  company: {
    name: string;
    tarif: string;
  };
};

const aiSummaries = reactive<AiSummary[]>([]);
const searchName = ref("");
const filterTarif = ref("");
const currentPage = ref(1);
const pageSize = ref(50);
const loading = ref(true);
const finished = ref(false);

async function getAiSummaries(next = false) {
  if (!next) {
    finished.value = false;
    loading.value = true;
  }
  const skip = (currentPage.value - 1) * pageSize.value;
  const params: Record<string, any> = {
    take: pageSize.value,
    skip,
  };
  if (searchName.value) {
    params.name = searchName.value;
  }
  if (filterTarif.value) {
    params.tarif = filterTarif.value;
  }
  if (next && finished.value) {
    loading.value = false;
    return;
  }
  try {
    const resp = (await AdminFetcher.get({
      path: "/features/ai_summary/all",
      query: params,
    })) as {
      companyId: string;
      company: {
        name: string;
        tarif: string;
      };
      ai_summary: boolean;
    }[];
    if (!next) {
      aiSummaries.length = 0;
    }
    if (resp.length === 0) {
      finished.value = true;
    }
    aiSummaries.push(
      ...resp.map((item) => {
        return {
          companyId: item.companyId,
          companyName: item.company.name,
          companyTarif: item.company.tarif,
          company: item.company,
          ai_summary: item.ai_summary,
        };
      }),
    );
    loading.value = false;
  } catch {
    toastErr("Ошибка загрузки данных");
  }
}

getAiSummaries();

async function enableAll() {
  try {
    await AdminFetcher.post({
      path: "/features/ai_summary/all",
      payload: {
        name: searchName.value || undefined,
        tarif: filterTarif.value || undefined,
        ai_summary: true,
      },
    });
    aiSummaries.length = 0;
    currentPage.value = 1;
    getAiSummaries();
    toastOk("Активировано");
  } catch {
    toastErr("Ошибка активации");
  }
}

async function disableAll() {
  try {
    await AdminFetcher.post({
      path: "/features/ai_summary/all",
      payload: {
        name: searchName.value || undefined,
        tarif: filterTarif.value || undefined,
        ai_summary: false,
      },
    });
    aiSummaries.length = 0;
    currentPage.value = 1;
    getAiSummaries();
    toastOk("Отключено");
  } catch {
    toastErr("Ошибка отключения");
  }
}

async function toggleAiSummary(companyId: string, currentAiSummary: boolean) {
  try {
    await AdminFetcher.post({
      path: `/features/ai_summary/${companyId}`,
      payload: {
        newValue: !currentAiSummary,
      },
    });
    // getAiSummaries();
    if (currentAiSummary) {
      toastOk("Отключено");
    } else {
      toastOk("Включено");
    }
  } catch {
    if (currentAiSummary) {
      toastErr("Ошибка отключения");
    } else {
      toastErr("Ошибка включения");
    }
  }
}
const tariffFilterActive = reactive(["FREE", "PAY"]);

function createColumns() {
  const cols: DataTableColumns<RowData> = [
    {
      title: "Название компании",
      key: "companyName",
    },
    {
      title: "Тариф",
      key: "companyTarif",
      filterOptions: [
        { label: "FREE", value: "FREE" },
        { label: "PAY", value: "PAY" },
      ],
      filterOptionValues: tariffFilterActive,
      filter: true,
    },
    {
      title: "Статус AI Summary",
      key: "ai_summary",
      render(row: RowData) {
        return row.ai_summary ? "Включено" : "Отключено";
      },
    },
    {
      title: "Действия",
      key: "actions",
      render(row: RowData) {
        return h(
          NButton,
          {
            strong: true,
            secondary: true,
            type: row.ai_summary ? "error" : "info",
            onClick: () => {
              toggleAiSummary(row.companyId, row.ai_summary).then(() => {
                row.ai_summary = !row.ai_summary;
              })
            },
          },
          {
            default: () => (row.ai_summary ? "Отключить" : "Включить"),
          },
        );
      },
    },
  ];
  return cols;
}
const columns = createColumns();
const updFilter = (filters: DataTableFilterState) => {
  tariffFilterActive.length = 0;
  const f = filters.companyTarif as ("FREE" | "PAY")[];
  let nextFilter = "";
  if ((f.includes("FREE") && f.includes("PAY")) || f.length === 0) {
    tariffFilterActive.push("FREE");
    tariffFilterActive.push("PAY");
    nextFilter = "";
  } else if (f.includes("FREE")) {
    tariffFilterActive.push("FREE");
    nextFilter = "FREE";
  } else if (f.includes("PAY")) {
    tariffFilterActive.push("PAY");
    nextFilter = "PAY";
  }
  if (nextFilter !== filterTarif.value) {
    filterTarif.value = nextFilter;
    aiSummaries.length = 0;
    currentPage.value = 1;
    getAiSummaries();
  }
};
const searchFilter = () => {
  currentPage.value = 1;
  aiSummaries.length = 0;
  getAiSummaries();
};
let block = false;
const scrollHandle = (e: Event) => {
  if (block) return;
  block = true;
  const target = e.target as HTMLDivElement;
  const scrollHeight = target.scrollHeight;
  const scrollTop = target.scrollTop;
  const clientHeight = target.clientHeight;
  if (scrollTop + clientHeight >= scrollHeight - 500) {
    nextifbottom(target).then(() => {
      block = false;
    });
  } else {
    block = false;
  }
};
const nextifbottom = async (target: HTMLDivElement) => {
  const scrollHeight = target.scrollHeight;
  const scrollTop = target.scrollTop;
  const clientHeight = target.clientHeight;
  if (scrollTop + clientHeight >= scrollHeight - 500) {
    currentPage.value++;
    await getAiSummaries(true).then(() => {
      if (!finished.value) {
        return nextifbottom(target);
      }
    });
  }
};
</script>

<template>
  <AdminLayout>
    <template #header>
      <h4>Доступ компаний</h4>
    </template>
    <div class="ai-summary-page">
      <NSpace :wrap-item="false" class="actions">
        <NInputGroup>
          <NInput
            v-model:value="searchName"
            placeholder="Поиск по названию компании"
          />
          <NButton strong secondary type="primary" @click="searchFilter"
            >Поиск</NButton
          >
        </NInputGroup>
        <NButton strong secondary type="primary" @click="enableAll"
          >Включить отфильтрованным</NButton
        >
        <NButton strong secondary type="error" @click="disableAll"
          >Отключить отфильтрованным</NButton
        >
      </NSpace>
      <div class="table">
        <NDataTable
          :columns="columns"
          :data="aiSummaries"
          :loading="loading"
          @update:filters="updFilter"
          @scroll="scrollHandle"
          remote
          :style="{
            height: '100%',
          }"
          flex-height
        ></NDataTable>
      </div>
    </div>
  </AdminLayout>
</template>

<style lang="scss" scoped>
.ai-summary-page {
  padding: 20px;
  height: 100px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;

  .actions {
    margin-bottom: 20px;
  }

  .table {
    height: 100px;
    flex-grow: 1;
  }

  .header {
    margin-bottom: 20px;

    h2 {
      color: #333;
      font-size: 1.5rem;
    }
  }

  .filters {
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    input[type="text"],
    select {
      padding: 0.5rem;
      margin-right: 10px;
      font-size: 1rem;
      border: 1px solid #ccc;
      border-radius: 4px;
    }

    button {
      padding: 0.5rem 1rem;
      background: #5b9bd5;
      color: #fff;
      border: none;
      border-radius: 4px;
      font-size: 1rem;
      cursor: pointer;
      transition: background 0.3s;

      &:hover {
        background: #4a8ac2;
      }
    }
  }

  .bulk-actions {
    margin-bottom: 20px;
    display: flex;
    gap: 12px;
  }

  .ai-summary-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 20px;

    th,
    td {
      padding: 10px;
      border: 1px solid #eee;
      text-align: left;
    }

    th {
      background: #f9f9f9;
      font-weight: bold;
    }
  }

  .pagination {
    display: flex;
    align-items: center;

    button {
      padding: 0.5rem 1rem;
      background: #5b9bd5;
      color: #fff;
      border: none;
      border-radius: 4px;
      font-size: 0.9rem;
      cursor: pointer;
      transition: background 0.3s;

      &:disabled {
        background: #ccc;
        cursor: not-allowed;
      }

      &:hover:not(:disabled) {
        background: #4a8ac2;
      }
    }

    span {
      margin: 0 10px;
    }
  }
}
.green-button {
  padding: 0.5rem 1rem;
  background: #2ecc71;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.3s;

  &:hover {
    background: #27ae60;
  }
}

.red-button {
  padding: 0.5rem 1rem;
  background: #e74c3c;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.3s;

  &:hover {
    background: #c0392b;
  }
}
</style>
